import { makeStyles, shorthands, tokens } from '@coreai/fluentui-react';
import { ottoTokens } from '@/theme/ottoTokens';

/**
 * Shared styles for the centered floating panels used by Stage 1
 * (SavingDraftOverlay) and Stage 3 (WorkflowCreatedPrompt) of the
 * Create-new-workflow flow.
 *
 * Visual contract (matching design):
 *  - Centered on screen — both surfaces communicate something the user
 *    must engage with (active save, or a decision to make). Centering
 *    matches the universal "look here" position for modal-like surfaces.
 *  - Subtle backdrop on the wrapper so peripheral content recedes
 *    visually without disappearing.
 *  - Neutral surface with subtle layered shadow (no solid border) so the
 *    card reads as elevated above the canvas underneath.
 *  - Concentric border-radii: outer borderRadiusLarge, inner buttons use
 *    borderRadiusMedium.
 *  - Soft enter (opacity + scale 0.97 → 1) — Emil's "never animate from
 *    scale(0)" rule. Strong custom ease-out curve for snap.
 *  - Respects `prefers-reduced-motion` by collapsing to a plain fade.
 *  - `transition-property` is enumerated (never `all`).
 */
export const useFloatingPanelStyles = makeStyles({
  /**
   * Fixed full-viewport wrapper that centers the panel and dims the
   * surrounding canvas. `pointer-events: auto` on the wrapper means
   * clicks outside the card hit the backdrop instead of falling
   * through to the canvas — preserving the surface's modal feel
   * while still leaving the canvas readable underneath.
   */
  viewport: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalL,
    pointerEvents: 'auto',
    zIndex: 10001,
    backgroundColor: ottoTokens.overlayBackdrop,
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: tokens.durationFast,
    animationTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
    },
  },

  panel: {
    width: '100%',
    maxWidth: '460px',
    minWidth: '320px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    // Layered shadow > border for elevation: scales with theme and reads
    // as a real surface against either canvas or page background.
    boxShadow: tokens.shadow28,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    // Soft enter: opacity 0 → 1, scale 0.97 → 1. Never `scale(0)` — Emil's
    // "nothing in the real world appears from nothing" rule. Strong
    // ease-out for the punchy initial movement.
    animationName: {
      from: { opacity: 0, transform: 'scale(0.97)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'both',
    transitionProperty: 'transform, opacity',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      animationName: {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      animationDuration: '0.01ms',
      transitionDuration: '0.01ms',
    },
    '@media (forced-colors: active)': {
      ...shorthands.border('1px', 'solid', 'CanvasText'),
    },
  },

  /** Compact title row (no DialogTitle chrome) */
  title: {
    margin: 0,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
    // Avoid orphans on the second line of slightly longer copy.
    textWrap: 'balance',
  },

  body: {
    margin: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    // Pretty wrap reduces ragged right edges and orphans in body text.
    textWrap: 'pretty',
  },

  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginBlockStart: tokens.spacingVerticalXS,
  },
});
